import React, { createRef, PureComponent } from 'react';
import { object, func, oneOfType, array, any, node, bool, arrayOf, string } from 'prop-types';
import { Table, TableBody, TableCell, TableHead, TableRow, Box } from '@mui/material';
import { Checkbox, Grid, TableFooter, TablePagination } from '@mui/material';
import { injectIntl } from 'react-intl';
import { IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Tooltip } from '@mui/material';
import { isNil, map } from 'lodash';
import { PAGINATION_PER_PAGE } from 'constants/Values';
import Progress from 'components/Progress';

import {
  allowedRoles,
  sortedColumnKey,
  sortKeyOf,
  utilGet,
  utilGetWithDefault,
} from 'util/tableUtils';
import { SimpleText } from 'util/IntlMessages';
import { Button } from '../Button';
import styles from './styles.module.scss';

export const ACTION_TYPE = {
  NAV: 'nav',
  ICON: 'icon',
  BUTTON: 'button',
  BUTTON_ICON: 'button-icon',
  ROW_SELECT: 'row-select',
};

class DefinableTable extends PureComponent {
  static propTypes = {
    tableDef: object,
    data: oneOfType([object, array]),
    userRoles: array,
    intl: object.isRequired,
    onActionClick: func,
    onSelectedRowUpdateRef: func,
    selectedRowId: any,
    selectedRowIds: arrayOf(any),
    children: node,
    detailPanelCollapsed: bool,
    onDetailPanelToggle: func,
    footerContent: node,
    multiSelect: bool,
    invertMultiSelect: bool,
    selectNoneTooltipIntlKey: string,
    selectAllTooltipIntlKey: string,
    loading: bool,
  };

  static defaultProps = {
    onActionClick: () => {},
    onSelectedRowUpdateRef: () => {},
    onDetailPanelToggle: () => {},
    multiSelect: false,
    loading: false,
  };

  constructor(props) {
    super(props);
    this.selectedRowRef = createRef();

    this.state = {
      sortColumns: props.tableDef.defaultSort || [],
      page: 0,
      rowsPerPage: PAGINATION_PER_PAGE[0],
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedRowId !== this.props.selectedRowId) {
      this.props.onSelectedRowUpdateRef(this.selectedRowRef);
    }
  }

  getSelectAllStatusAndTooltip = () => {
    const { tableDef, data, multiSelect, selectedRowIds, invertMultiSelect } = this.props;
    let selectAllChecked = false;
    let selectAllTooltip = {};

    if (multiSelect) {
      const rowData = utilGet(data, tableDef.rows.source);
      selectAllChecked = rowData && rowData.length > 0 && rowData.length === selectedRowIds.length;
      selectAllChecked = isNil(selectAllChecked) ? false : selectAllChecked;
      selectAllChecked = invertMultiSelect ? !selectAllChecked : selectAllChecked;
      const id = selectAllChecked
        ? tableDef.selectNoneTooltipIntlKey || 'eca.action.select-none.tooltip'
        : tableDef.selectAllTooltipIntlKey || 'eca.action.select-all.tooltip';
      selectAllTooltip = { id };
    }

    return { selectAllChecked, selectAllTooltip };
  };

  rowClasses = row => {
    const { tableDef, selectedRowId } = this.props;
    if (!isNil(tableDef.rows.rowAction)) {
      const classes = [styles.row];
      if (tableDef.rows.highlightSelected && selectedRowId === row.id) {
        classes.push(styles.stickySelect, styles.selected);
      }
      return classes.join(' ');
    }
    return '';
  };

  createRowClickHandler = row => {
    const { tableDef, onActionClick } = this.props;
    if (!isNil(tableDef.rows.rowAction)) {
      return e => {
        onActionClick({ row, ...tableDef.rows.actions[tableDef.rows.rowAction], domEvent: e });
      };
    }
    return undefined;
  };

  createRowDblClickHandler = row => {
    const { tableDef, onActionClick } = this.props;
    if (!isNil(tableDef.rows.rowDblClkAction)) {
      return e => {
        onActionClick({
          row,
          ...tableDef.rows.actions[tableDef.rows.rowDblClkAction],
          domEvent: e,
        });
      };
    }
    return undefined;
  };

  createRowSelectHandler = row => {
    const { tableDef, onActionClick } = this.props;
    return e => {
      const { selectedRowIds } = this.props;
      let newSelection;
      if (selectedRowIds.includes(row.id)) {
        newSelection = selectedRowIds.filter(rowId => rowId !== row.id);
      } else {
        newSelection = [...selectedRowIds, row.id];
      }
      onActionClick({
        rowIds: newSelection,
        ...tableDef.rows.multiSelectAction,
        domEvent: e,
      });
    };
  };

  handleSelectAll = e => {
    const { data, selectedRowIds, onActionClick, tableDef } = this.props;
    const rowData = utilGet(data, tableDef.rows.source);
    const rowIds = rowData.length === selectedRowIds.length ? [] : map(rowData, 'id');
    onActionClick({
      rowIds,
      ...tableDef.rows.multiSelectAction,
      domEvent: e,
    });
  };

  // ── AN ACCESSOR IS NOT A COLUMN IDENTITY ─────────────────────────────────
  //
  // `sortColumns[].source` is what to ORDER BY, and two columns may legitimately order by
  // the same field: ECA history shows a date and a time that are both formats OF
  // `startTime`, so both carry `sortSource: 'startTime'`. Matching the indicator on the
  // source alone therefore drew an arrow on BOTH of them.
  //
  // So the sorted COLUMN is tracked separately from the sort KEY. `colKey` is the column's
  // `headerIntlKey`, which is unique within a def and stable across renders — the column
  // object itself cannot be stored, since `defaultSort` is authored in the table def with no
  // column reference at all and has to keep working.
  handleSort = colDef => {
    const sortKey = sortKeyOf(colDef);
    const colKey = colDef.headerIntlKey;
    this.setState(prevState => {
      const existingSortIndex = prevState.sortColumns.findIndex(col => col.source === sortKey);
      let newSortColumns;

      if (existingSortIndex === 0) {
        // If the clicked column is already the primary sort column, reverse its direction
        const existingSort = prevState.sortColumns[0];
        const newDirection = existingSort.direction === 'asc' ? 'desc' : 'asc';
        // If the click landed on a DIFFERENT column that happens to share this source
        // (a date and a time both derived from startTime), move the arrow to it rather
        // than silently reversing the other one.
        const sameColumn = existingSort.colKey === undefined || existingSort.colKey === colKey;
        newSortColumns = [
          {
            ...existingSort,
            colKey,
            direction: sameColumn ? newDirection : existingSort.direction,
          },
          ...prevState.sortColumns.slice(1),
        ];
      } else {
        // If the clicked column is not the primary sort column, make it the primary with ascending order
        const newPrimarySort = { source: sortKey, direction: 'asc', colKey };
        newSortColumns = [
          newPrimarySort,
          ...prevState.sortColumns.filter(col => col.source !== sortKey),
        ];
      }

      return { sortColumns: newSortColumns };
    });
  };

  // Exactly one column may carry the arrow. `colKey` names it once a header has been
  // clicked; `defaultSort` carries no column, so on first render the arrow goes to the FIRST
  // column whose sort key matches — deterministic, and the alternative is the two-arrow bug.
  getSortIndicator = colDef => {
    const { sortColumns } = this.state;
    if (!sortColumns.length) return null;
    const { tableDef } = this.props;
    const owner = sortedColumnKey((tableDef && tableDef.columns) || [], sortColumns[0]);
    if (owner !== colDef.headerIntlKey) return null;
    return sortColumns[0].direction === 'asc' ? '▲' : '▼';
  };

  getSortedData = () => {
    const { sortColumns } = this.state;
    const { intl, data, tableDef } = this.props;
    const rowData = utilGet(data, tableDef.rows.source);

    if (!rowData || rowData.length === 0 || sortColumns.length === 0) {
      return rowData;
    }

    const sortedData = [...rowData];
    return sortedData.sort((a, b) => {
      for (let i = 0; i < sortColumns.length; i++) {
        const { source, direction } = sortColumns[i];
        let valueA = utilGet(a, source, intl);
        let valueB = utilGet(b, source, intl);

        // Sort nulls to the top
        if (valueA === null || valueA === undefined) return direction === 'asc' ? -1 : 1;
        if (valueB === null || valueB === undefined) return direction === 'asc' ? 1 : -1;

        // Ignore case for strings
        if (typeof valueA === 'string') valueA = valueA.toLowerCase();
        if (typeof valueB === 'string') valueB = valueB.toLowerCase();

        if (valueA < valueB) return direction === 'asc' ? -1 : 1;
        if (valueA > valueB) return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // nb: map params available: action, actionIndex, actions
  renderAction = (action, actionIndex) => {
    const {
      userRoles,
      onActionClick,
      intl,
      tableDef: { detailPanel },
      detailPanelCollapsed,
    } = this.props;
    const { actionDef, row } = action;

    if (allowedRoles(actionDef, userRoles)) {
      switch (actionDef.type) {
        case ACTION_TYPE.ICON:
          return (
            <Tooltip key={actionIndex} title={intl.formatMessage({ id: actionDef.tooltip })}>
              <IconButton
                style={actionDef.iconColor ? { color: actionDef.iconColor } : {}}
                onClick={e => onActionClick({ row, ...actionDef, domEvent: e })}
              >
                <actionDef.icon {...actionDef.iconProps} />
              </IconButton>
            </Tooltip>
          );
        case ACTION_TYPE.NAV:
          return (
            <Button
              key={actionIndex}
              size="large"
              component={Link}
              to={utilGet(row, actionDef.navUrl, action)}
              iconRight={<ArrowRightIcon />}
            >
              {intl.formatMessage({ id: actionDef.action })}
            </Button>
          );
        case ACTION_TYPE.BUTTON:
        case ACTION_TYPE.BUTTON_ICON:
          if (!detailPanel.collapsible || (detailPanel.collapsible && !detailPanelCollapsed)) {
            return null;
          }
          return (
            <Button
              key={actionIndex}
              size="large"
              onClick={e => onActionClick({ row, ...actionDef, domEvent: e })}
              color="primary"
            >
              {intl.formatMessage({ id: actionDef.action })}
            </Button>
          );
        default:
      }
    }
    return null;
  };

  renderTable() {
    const {
      tableDef,
      intl,
      footerContent,
      multiSelect,
      selectedRowIds,
      invertMultiSelect,
      selectedRowId,
      loading,
    } = this.props;
    const { page, rowsPerPage } = this.state;
    const { selectAllChecked, selectAllTooltip } = this.getSelectAllStatusAndTooltip();
    const sortedData = this.getSortedData();
    let paginatedData = sortedData;
    if (sortedData && sortedData.length > PAGINATION_PER_PAGE[0] * 2) {
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      paginatedData = sortedData.slice(startIndex, endIndex);
    }

    return (
      <div className={styles.root}>
        <Table className={styles.table}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              {multiSelect && (
                <TableCell style={{ width: '0%' }}>
                  <Tooltip title={intl.formatMessage(selectAllTooltip)} placement="bottom">
                    <Checkbox
                      color="primary"
                      checked={selectAllChecked}
                      onChange={this.handleSelectAll}
                      style={{
                        color: 'white',
                        margin: 12,
                        width: 24,
                        height: 18,
                      }}
                    />
                  </Tooltip>
                </TableCell>
              )}
              {tableDef.columns.map((colDef, colIndex) => (
                <TableCell
                  key={colIndex}
                  style={utilGetWithDefault(
                    colDef,
                    'styles',
                    colIndex < tableDef.columns.length - 1
                      ? { width: '1%', whiteSpace: 'nowrap', cursor: 'pointer' }
                      : { cursor: 'pointer' },
                    intl,
                    colIndex,
                    colDef,
                    tableDef,
                  )}
                  onClick={() => this.handleSort(colDef)}
                >
                  {intl.formatMessage({ id: colDef.headerIntlKey })}
                  {this.getSortIndicator(colDef) && (
                    <Box component="span" sx={{ ml: 0.5 }}>
                      {this.getSortIndicator(colDef)}
                    </Box>
                  )}
                </TableCell>
              ))}
              {tableDef.actionColIntlKey && (
                <TableCell style={{ width: '0.1%', whiteSpace: 'nowrap' }}>
                  {intl.formatMessage({ id: tableDef.actionColIntlKey })}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody className={styles.tableBody}>
            {!loading &&
              paginatedData &&
              paginatedData.length > 0 &&
              paginatedData.map((row, rowIndex) => (
                <TableRow key={rowIndex} className={this.rowClasses(row)}>
                  {multiSelect && (
                    <TableCell>
                      <Checkbox
                        color="primary"
                        checked={
                          invertMultiSelect
                            ? !selectedRowIds.includes(row.id)
                            : selectedRowIds.includes(row.id)
                        }
                        onChange={this.createRowSelectHandler(row)}
                      />
                    </TableCell>
                  )}
                  {tableDef.columns.map((colDef, colIndex) => (
                    <TableCell
                      key={colIndex}
                      onClick={
                        (!colDef.preventRowClick && this.createRowClickHandler(row)) || undefined
                      }
                      onDoubleClick={
                        (!colDef.preventRowClick && this.createRowDblClickHandler(row)) || undefined
                      }
                      // style={
                      //   colIndex < tableDef.columns.length - 1
                      //     ? { width: '1%', whiteSpace: 'nowrap' }
                      //     : undefined
                      // }
                    >
                      {selectedRowId === row.id && <div ref={this.selectedRowRef} />}
                      {utilGet(
                        row,
                        colDef.rowCellSource,
                        intl,
                        rowIndex,
                        colIndex,
                        colDef,
                        tableDef,
                      )}
                    </TableCell>
                  ))}
                  {tableDef.actionColIntlKey && (
                    <TableCell
                      style={{ width: '0.1%', whiteSpace: 'nowrap' }}
                      className={styles.actionsCell}
                    >
                      <div className={styles.actionsWrapper}>
                        {tableDef.rows.actions
                          .filter(actionDef => !actionDef.conditional || actionDef.conditional(row))
                          .map(actionDef => ({ actionDef, row }))
                          .map(this.renderAction)}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!loading &&
              !(paginatedData && paginatedData.length > 0) && (
                <TableRow>
                  <TableCell
                    colSpan={
                      tableDef.columns.length +
                      (multiSelect ? 1 : 0) +
                      (tableDef.actionColIntlKey ? 1 : 0)
                    }
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <SimpleText id={tableDef.rows.noDataIntlKey || 'eca.heading.no-data'} />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={
                    tableDef.columns.length +
                    (multiSelect ? 1 : 0) +
                    (tableDef.actionColIntlKey ? 1 : 0)
                  }
                >
                  <Progress textId={tableDef.rows.loadingIntlKey} positionRelative />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {sortedData &&
            sortedData.length > PAGINATION_PER_PAGE[0] * 2 && (
              <TableFooter>
                <TableRow>{this.renderTablePagination()}</TableRow>
              </TableFooter>
            )}
        </Table>
        {footerContent}
      </div>
    );
  }

  renderTablePagination = () => {
    const { intl, data, tableDef } = this.props;
    const rowData = utilGet(data, tableDef.rows.source);
    return (
      <TablePagination
        count={(rowData && rowData.length) || 0}
        onChangePage={(e, page) => {
          this.setState({ page });
        }}
        onChangeRowsPerPage={e => {
          this.setState({ rowsPerPage: e.target.value });
        }}
        page={this.state.page || 0}
        rowsPerPage={this.state.rowsPerPage || PAGINATION_PER_PAGE[0]}
        rowsPerPageOptions={PAGINATION_PER_PAGE}
        labelRowsPerPage={intl.formatMessage({
          id: 'component.table.pagination.labelRowsPerPage',
        })}
      />
    );
  };

  render() {
    const { tableDef, children, detailPanelCollapsed, onDetailPanelToggle, intl } = this.props;
    const { detailPanel } = tableDef;
    if (detailPanel.gridCols) {
      return (
        <div className={styles.outer}>
          <div className={styles.innerGrid}>
            <Grid container spacing={1} sx={{ flexGrow: 1, flexWrap: 'nowrap' }}>
              <Grid style={{ maxHeight: '100%' }} size="grow">
                {this.renderTable()}
              </Grid>
              {(!detailPanel.collapsible || (detailPanel.collapsible && !detailPanelCollapsed)) && (
                <Grid sx={{ height: '100%' }} size={detailPanel.gridCols}>
                  <div className={styles.detailPanelContainer}>
                    {detailPanel.collapsible && (
                      <div className={styles.detailPanelHeader}>
                        <Tooltip
                          title={intl.formatMessage({ id: detailPanel.collapseTooltipIntlKey })}
                        >
                          <IconButton onClick={onDetailPanelToggle}>
                            <detailPanel.collapseIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                    <div className={styles.detailPanelChildrenContainer}>{children}</div>
                  </div>
                </Grid>
              )}
            </Grid>
          </div>
          {detailPanel.collapsible &&
            detailPanelCollapsed && (
              <div className={styles.innerDetails}>
                <Tooltip title={intl.formatMessage({ id: detailPanel.expandTooltipIntlKey })}>
                  <IconButton onClick={onDetailPanelToggle}>
                    <detailPanel.expandIcon />
                  </IconButton>
                </Tooltip>
              </div>
            )}
        </div>
      );
    }

    return this.renderTable();
  }
}

export default injectIntl(DefinableTable);
